import { create } from 'zustand';
import { ToastVariant, ToastPosition } from '../components/ui/Toast';

export interface ToastNotification {
  id: string;
  variant: ToastVariant;
  title?: string;
  message: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
  createdAt: number;
}

export type NotificationInput = Omit<ToastNotification, 'id' | 'createdAt'>;

interface NotificationsState {
  notifications: ToastNotification[];
  position: ToastPosition;
  maxNotifications: number;
  defaultDuration: number;

  // Actions
  addNotification: (notification: NotificationInput) => string;
  removeNotification: (id: string) => void;
  clearAll: () => void;
  setPosition: (position: ToastPosition) => void;
  setMaxNotifications: (max: number) => void;
  setDefaultDuration: (duration: number) => void;

  // Convenience methods
  success: (message: string, options?: Partial<NotificationInput>) => string;
  error: (message: string, options?: Partial<NotificationInput>) => string;
  warning: (message: string, options?: Partial<NotificationInput>) => string;
  info: (message: string, options?: Partial<NotificationInput>) => string;
}

// Generate unique ID
const generateId = (): string => {
  return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

export const useNotificationsStore = create<NotificationsState>((set, get) => ({
  notifications: [],
  position: 'top-right',
  maxNotifications: 5,
  defaultDuration: 5000,

  addNotification: (notification) => {
    const id = generateId();
    const { maxNotifications, defaultDuration, notifications } = get();

    const newNotification: ToastNotification = {
      ...notification,
      id,
      duration: notification.duration ?? defaultDuration,
      createdAt: Date.now(),
    };

    set({
      notifications: [
        newNotification,
        // Limit notifications to max
        ...notifications.slice(0, maxNotifications - 1),
      ],
    });

    return id;
  },

  removeNotification: (id) => {
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    }));
  },

  clearAll: () => {
    set({ notifications: [] });
  },

  setPosition: (position) => {
    set({ position });
  },

  setMaxNotifications: (maxNotifications) => {
    set({ maxNotifications });
  },

  setDefaultDuration: (defaultDuration) => {
    set({ defaultDuration });
  },

  // Convenience methods
  success: (message, options = {}) => {
    return get().addNotification({
      variant: 'success',
      message,
      ...options,
    });
  },

  error: (message, options = {}) => {
    return get().addNotification({
      variant: 'error',
      message,
      duration: 0, // Errors don't auto-dismiss by default
      ...options,
    });
  },

  warning: (message, options = {}) => {
    return get().addNotification({
      variant: 'warning',
      message,
      ...options,
    });
  },

  info: (message, options = {}) => {
    return get().addNotification({
      variant: 'info',
      message,
      ...options,
    });
  },
}));

// Selector hooks for better performance
export const useNotifications = () =>
  useNotificationsStore((state) => state.notifications);

export const useNotificationPosition = () =>
  useNotificationsStore((state) => state.position);

export const useNotificationActions = () =>
  useNotificationsStore((state) => ({
    addNotification: state.addNotification,
    removeNotification: state.removeNotification,
    clearAll: state.clearAll,
    success: state.success,
    error: state.error,
    warning: state.warning,
    info: state.info,
  }));
